import React, { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery, gql } from "@apollo/client";
import StatusBadge from "../components/StatusBadge";
import Stars from "../components/Stars";
import { blendedRating } from "../utils/reviewScore";
import { useCurrentUser } from "../context/AuthContext";
import { API_URL } from "../config";
import ProfileCard from "../components/ProfileCard";
import { nameFor } from "../utils/displayName";

const GET_BOOKING = gql`
  query BookingDetail($id: Int!) {
    bookingById(id: $id) {
      id startAt endAt status totalCents grandTotalCents hostPayoutCents hostFeeCents serviceFeeCents createdAt
      chatThreadByBookingId { id }
      reviewsByBookingId {
        nodes {
          id reviewerId reviewerRole overall cleanliness communication
          accuracy valueScore respectRules body createdAt
          userByReviewerId { userProfileByUserId { fullName displayName } }
        }
      }
      saunaBySaunaId {
        id name description addressLine city state country pricePerHourCents
        saunaImagesBySaunaId(orderBy: DISPLAY_ORDER_ASC, first: 1) {
          nodes { id url }
        }
        saunaAmenitiesBySaunaId {
          nodes { amenityByAmenityId { id label } }
        }
        userByOwnerId {
          id createdAt phone
          userProfileByUserId { fullName displayName hometown school work interests bio }
        }
      }
      userByRenterId {
        id createdAt phone
        userProfileByUserId { fullName displayName hometown school work interests bio preferredSaunaTempF }
      }
    }
  }
`;

async function bookingAction(path: string): Promise<void> {
  const token = sessionStorage.getItem("jwt");
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error ?? "Request failed");
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmt(dateStr: string) {
  return new Date(dateStr).toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SaunaSummaryCard({ sauna }: {
  sauna: {
    id: number; name: string; description?: string;
    addressLine?: string; city?: string; state?: string; country?: string;
    pricePerHourCents: number;
    saunaImagesBySaunaId: { nodes: { id: number; url: string }[] };
    saunaAmenitiesBySaunaId: { nodes: { amenityByAmenityId: { id: number; label: string } }[] };
  };
}) {
  const image = sauna.saunaImagesBySaunaId?.nodes?.[0];
  const address = [sauna.addressLine, sauna.city, sauna.state, sauna.country].filter(Boolean).join(", ");
  const amenities = (sauna.saunaAmenitiesBySaunaId?.nodes ?? [])
    .map((n: any) => n.amenityByAmenityId).filter(Boolean);

  return (
    <div style={card}>
      <p style={cardLabel}>Sauna</p>
      {image && (
        <img
          src={`${API_URL}${image.url}`}
          alt={sauna.name}
          style={{ width: "100%", height: 180, objectFit: "cover", borderRadius: 8, marginBottom: 12 }}
        />
      )}
      <Link to={`/saunas/${sauna.id}`} style={{ fontWeight: 700, fontSize: 16, color: "#111827", textDecoration: "none" }}>
        {sauna.name}
      </Link>
      {address && <p style={{ margin: "4px 0 4px", fontSize: 13, color: "#6b7280" }}>{address}</p>}
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#374151" }}>
        ${(sauna.pricePerHourCents / 100).toFixed(2)} / hr
      </p>
      {sauna.description && (
        <p style={{ margin: "0 0 10px", fontSize: 14, lineHeight: 1.6, color: "#374151" }}>
          {sauna.description}
        </p>
      )}
      {amenities.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {amenities.map((a: any) => (
            <span key={a.id} style={amenityPill}>{a.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

type ReviewNode = {
  id: number;
  reviewerId: number;
  reviewerRole: string;
  overall: number;
  cleanliness?: number | null;
  communication?: number | null;
  accuracy?: number | null;
  valueScore?: number | null;
  respectRules?: number | null;
  body?: string | null;
  createdAt: string;
  userByReviewerId?: { userProfileByUserId?: { fullName?: string; displayName?: string | null } | null } | null;
};

function ReviewCard({ review, label }: { review: ReviewNode; label: string }) {
  const reviewer = review.userByReviewerId;
  const name = nameFor(reviewer?.userProfileByUserId, "User");
  const role = review.reviewerRole as "guest" | "host";

  const criteria = [{
    label: "Cleanliness", value: review.cleanliness,
  }, {
    label: "Communication", value: review.communication,
  }, ...(role === "guest" ? [
    { label: "Accuracy", value: review.accuracy },
    { label: "Value",    value: review.valueScore },
  ] : [
    { label: "House rules", value: review.respectRules },
  ])].filter((c) => c.value != null);

  const blended = blendedRating(review);
  return (
    <div style={card}>
      <p style={cardLabel}>{label}</p>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Stars rating={blended} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>{blended.toFixed(1)}/5 overall</span>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>
          · {name} · {new Date(review.createdAt).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
        </span>
      </div>
      {criteria.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 20px", marginBottom: review.body ? 8 : 0 }}>
          {criteria.map((c) => (
            <span key={c.label} style={{ fontSize: 12, color: "#6b7280" }}>
              {c.label} <Stars rating={c.value!} />
            </span>
          ))}
        </div>
      )}
      {review.body && (
        <p style={{ margin: 0, fontSize: 14, color: "#374151", lineHeight: 1.5 }}>{review.body}</p>
      )}
    </div>
  );
}

function ReviewSection({
  currentUserId,
  reviews,
  counterpartLabel,
}: {
  currentUserId: number;
  reviews: ReviewNode[];
  counterpartLabel: string;
}) {
  const myReview = reviews.find((r) => r.reviewerId === currentUserId);
  const theirReview = reviews.find((r) => r.reviewerId !== currentUserId);

  return (
    <>
      {myReview && <ReviewCard review={myReview} label="Your review" />}
      {theirReview && <ReviewCard review={theirReview} label={`${counterpartLabel}'s review`} />}
    </>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function BookingDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const justPaid = searchParams.get("redirect_status") === "succeeded";
  const currentUserId = useCurrentUser();
  const { data, loading, error, refetch } = useQuery(GET_BOOKING, {
    variables: { id: parseInt(id!, 10) },
  });
  const [cancelling,  setCancelling]  = useState(false);
  const [approving,   setApproving]   = useState(false);
  const [declining,   setDeclining]   = useState(false);

  if (loading) return <p style={{ padding: 24 }}>Loading...</p>;
  if (error)   return <p style={{ padding: 24, color: "#ef4444" }}>Error: {error.message}</p>;

  const b = data?.bookingById;
  if (!b) return <p style={{ padding: 24 }}>Booking not found.</p>;

  const ownerId  = b.saunaBySaunaId?.userByOwnerId?.id;
  const renterId = b.userByRenterId?.id;
  if (currentUserId !== ownerId && currentUserId !== renterId) {
    return <p style={{ padding: 24, color: "#ef4444" }}>You don't have access to this booking.</p>;
  }

  const isHost    = currentUserId === ownerId;
  const sauna     = b.saunaBySaunaId;
  const threadId  = b.chatThreadByBookingId?.id;
  const canCancel = ["pending_payment", "pending_approval", "confirmed"].includes(b.status);
  const stayEnded = new Date(b.endAt) < new Date();
  const reviews: ReviewNode[] = b.reviewsByBookingId?.nodes ?? [];
  const counterpart = isHost
    ? nameFor(b.userByRenterId?.userProfileByUserId, "Guest")
    : nameFor(sauna?.userByOwnerId?.userProfileByUserId, "Host");
  const myReview = reviews.find((r) => r.reviewerId === currentUserId);
  const reviewable = stayEnded && ["confirmed", "completed"].includes(b.status);

  async function handleCancel() {
    const msg = b.status === "confirmed"
      ? "Cancel this booking?\n\nPayment has already been charged. To request a refund, email craighalonen04@gmail.com after cancelling."
      : "Cancel this booking? Any payment authorization will be released automatically.";
    if (!window.confirm(msg)) return;
    setCancelling(true);
    try {
      await bookingAction(`/api/bookings/${id}/cancel`);
      await refetch();
    } catch (err: any) {
      alert(err?.message ?? "Failed to cancel booking");
    } finally {
      setCancelling(false);
    }
  }

  async function handleApprove() {
    setApproving(true);
    try {
      await bookingAction(`/api/bookings/${id}/approve`);
      await refetch();
    } catch (err: any) {
      alert(err?.message ?? "Failed to approve");
    } finally {
      setApproving(false);
    }
  }

  async function handleDecline() {
    if (!window.confirm("Decline this booking request?")) return;
    setDeclining(true);
    try {
      await bookingAction(`/api/bookings/${id}/decline`);
      await refetch();
    } catch (err: any) {
      alert(err?.message ?? "Failed to decline");
    } finally {
      setDeclining(false);
    }
  }

  return (
    <div style={{ maxWidth: 600, margin: "40px auto", padding: "0 24px 60px" }}>
      <Link to="/dashboard" style={{ fontSize: 13, color: "#2563EB", textDecoration: "none" }}>
        ← Back to dashboard
      </Link>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, marginBottom: 4 }}>
        <h2 style={{ margin: 0 }}>Booking #{b.id}</h2>
        <StatusBadge status={b.status} />
      </div>

      {justPaid && !isHost && b.status === "confirmed" && (
        <p style={bannerStyle("#f0fdf4", "#15803d")}>
          Payment received — your booking is confirmed. You'll receive a text confirmation shortly.
        </p>
      )}
      {justPaid && !isHost && b.status === "pending_approval" && (
        <p style={bannerStyle("#eff6ff", "#1d4ed8")}>
          Payment authorized — your request has been sent to the host. We'll text you as soon as they respond.
        </p>
      )}

      {/* Status banners */}
      {b.status === "pending_payment" && (
        <p style={bannerStyle("#f3f4f6", "#374151")}>
          {isHost
            ? "Waiting for the guest to complete payment. You'll be able to approve here once their payment is authorized."
            : "Payment is pending. Complete checkout to confirm this booking."}
        </p>
      )}
      {b.status === "pending_approval" && isHost && (
        <div style={{ ...bannerStyle("#fffbeb", "#92400e"), marginBottom: 16 }}>
          <strong style={{ display: "block", fontSize: 15, marginBottom: 4 }}>
            Action required — approve or decline this request
          </strong>
          <span style={{ fontSize: 13 }}>
            Approving captures the guest's payment authorization. Declining releases the hold.
          </span>
        </div>
      )}
      {b.status === "pending_approval" && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ ...bannerStyle("#fef9c3", "#d97706"), marginBottom: isHost ? 10 : 0 }}>
            {isHost
              ? "This booking is awaiting your approval."
              : "Waiting for host approval. You'll be notified once the host responds."}
          </p>
          {isHost && (
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={handleApprove}
                disabled={approving || declining}
                style={approveBtn}
              >
                {approving ? "Approving…" : "Approve"}
              </button>
              <button
                onClick={handleDecline}
                disabled={approving || declining}
                style={declineBtn}
              >
                {declining ? "Declining…" : "Decline"}
              </button>
            </div>
          )}
        </div>
      )}
      {b.status === "declined" && (
        <p style={bannerStyle("#fee2e2", "#dc2626")}>
          This booking was declined by the host.
        </p>
      )}

      {/* Post-stay review nudge */}
      {reviewable && !myReview && (
        <div style={{ ...bannerStyle("#eff6ff", "#1e40af"), display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <span>Your stay has ended — share your experience.</span>
          <Link to={`/bookings/${b.id}/review`} style={leaveReviewBtn}>
            Leave a review
          </Link>
        </div>
      )}

      {/* Booking summary */}
      <div style={{ ...card, marginTop: 16 }}>
        {!isHost && sauna && (
          <div style={{ marginBottom: 12 }}>
            <Link to={`/saunas/${sauna.id}`} style={{ fontWeight: 600, color: "#111827", textDecoration: "none", fontSize: 15 }}>
              {sauna.name}
            </Link>
            {[sauna.addressLine, sauna.city, sauna.state, sauna.country].filter(Boolean).join(", ") && (
              <p style={{ margin: "2px 0 0", fontSize: 13, color: "#6b7280" }}>
                {[sauna.addressLine, sauna.city, sauna.state, sauna.country].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        )}
        {isHost && sauna && (
          <div style={{ marginBottom: 12 }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>{sauna.name}</p>
          </div>
        )}
        <Row label="Check-in"  value={fmt(b.startAt)} />
        <Row label="Check-out" value={fmt(b.endAt)} />
        {isHost ? (
          <>
            {b.hostFeeCents != null && (
              <Row label="Host fee (6%)" value={`-$${(b.hostFeeCents / 100).toFixed(2)}`} />
            )}
            <Row label="You receive" value={`$${((b.hostPayoutCents ?? b.totalCents) / 100).toFixed(2)}`} />
          </>
        ) : (
          <>
            {b.serviceFeeCents != null && (
              <Row label="Service fee (6%)" value={`$${(b.serviceFeeCents / 100).toFixed(2)}`} />
            )}
            <Row label="You paid" value={`$${((b.grandTotalCents ?? b.totalCents) / 100).toFixed(2)}`} />
          </>
        )}
        <Row label="Booked on" value={new Date(b.createdAt).toLocaleDateString()} />
      </div>

      {/* Host view: show who booked */}
      {isHost && b.userByRenterId && (
        <ProfileCard heading="Guest" user={b.userByRenterId} sinceLabel="Member" showPhone={b.status === "confirmed"} style={{ marginTop: 16 }} />
      )}

      {/* Renter view: sauna details + host profile */}
      {!isHost && (
        <>
          {sauna && <SaunaSummaryCard sauna={sauna} />}
          {sauna?.userByOwnerId && (
            <ProfileCard heading="Your host" user={sauna.userByOwnerId} sinceLabel="Member" showPhone={b.status === "confirmed"} style={{ marginTop: 16 }} />
          )}
        </>
      )}

      {/* Reviews — shown once submitted */}
      {reviewable && (
        <ReviewSection
          currentUserId={currentUserId}
          reviews={reviews}
          counterpartLabel={counterpart}
        />
      )}

      {/* Message counterpart */}
      {threadId && (
        <Link to={`/messages/${threadId}`} style={messageBtn}>
          {isHost ? "Message guest" : "Message host"}
        </Link>
      )}

      {/* Cancel */}
      {canCancel && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={handleCancel}
            disabled={cancelling}
            style={cancelBtn}
          >
            {cancelling ? "Cancelling..." : "Cancel booking"}
          </button>
          {b.status === "confirmed" && (
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "#6b7280" }}>
              Payment has been charged. For refund requests, email{" "}
              <a href="mailto:craighalonen04@gmail.com" style={{ color: "#2563eb" }}>craighalonen04@gmail.com</a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Shared primitives ─────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14 }}>
      <span style={{ color: "#6b7280" }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

function bannerStyle(bg: string, color: string): React.CSSProperties {
  return { fontSize: 14, color, margin: "0 0 16px", background: bg, padding: "8px 12px", borderRadius: 6 };
}

// ── Styles ────────────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  border: "1px solid #e5e7eb", borderRadius: 8,
  padding: 20, marginTop: 16, background: "#fff",
};
const cardLabel: React.CSSProperties = {
  margin: "0 0 10px", fontSize: 11, fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 1, color: "#9ca3af",
};
const amenityPill: React.CSSProperties = {
  padding: "3px 10px", borderRadius: 20, fontSize: 12,
  background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
};
const messageBtn: React.CSSProperties = {
  display: "inline-block", marginTop: 20, padding: "10px 20px",
  background: "#fff", color: "#2563eb",
  border: "1px solid #2563eb", borderRadius: 6, fontSize: 14,
  textDecoration: "none", fontWeight: 500,
};
const cancelBtn: React.CSSProperties = {
  marginTop: 12, padding: "10px 20px", background: "#ef4444",
  color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
const approveBtn: React.CSSProperties = {
  padding: "9px 20px", background: "#16a34a", color: "#fff",
  border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14, fontWeight: 600,
};
const declineBtn: React.CSSProperties = {
  padding: "9px 20px", background: "#fff", color: "#ef4444",
  border: "1px solid #ef4444", borderRadius: 6, cursor: "pointer", fontSize: 14,
};
const leaveReviewBtn: React.CSSProperties = {
  padding: "8px 18px", background: "#2563eb", color: "#fff",
  border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600,
  textDecoration: "none", whiteSpace: "nowrap",
};
